import React, { useState, useEffect } from 'react';
import { Modal, Row, Col, Form } from 'react-bootstrap';
import Button from 'components/base/Button';
import { useHVAConversation } from './HVADynamicAgentConversationContext';
import HVADynamicConversationWidget from './HVADynamicConversationWidget';
import {
  HVADynamicConversationModalProps,
  FeedbackMessage
} from './HVADynamicAgentData';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

const HVADynamicConversationModalContent: React.FC<
  HVADynamicConversationModalProps
> = ({
  show,
  onHide,
  title,
  agentCode,
  agentName,
  agentImage,
  onError,
  customer,
  trn,
  dateOfBirth,
  debtamount,
  currency,
  origin,
  IBAN,
  firstMessage,
  prompt,
  voice,
  language,
  maxDiscount
}) => {
  const {
    setClientInfo,
    isNifValidated,
    isDobValidated,
    requestTransfer,
    setRequestTransfer,
    setIsNifValidated,
    setIsDobValidated
  } = useHVAConversation();

  // Inicializar clientInfo cuando se montan los props
  useEffect(() => {
    if (trn && customer && dateOfBirth && debtamount) {
      setClientInfo({
        voice: voice,
        trn: trn,
        name: customer,
        dateofbirth: new Date(dateOfBirth),
        debtamount: Number(debtamount),
        currency: currency || 'EUR',
        origin: origin || '',
        IBAN: IBAN || 'GR16 0110 1050 0025 4801 2345 678',
        maxDiscount: maxDiscount
      });
    }
  }, [trn, customer, dateOfBirth, debtamount, setClientInfo]);

  const [formData, setFormData] = useState({
    // Agent Configuration
    voice: voice,
    // Context Configuration
    amount: debtamount?.toString() || '0',
    currency: currency || 'EUR',
    maxDiscount: '15',
    debtDescription: origin || 'Credit card debt',
    // Customer Data
    trn: trn || '',
    name: customer || '',
    dateofbirth: dateOfBirth
      ? new Date(dateOfBirth).toISOString().split('T')[0]
      : ''
  });

  const [feedbackMessages, setFeedbackMessages] = useState<FeedbackMessage[]>(
    []
  );

  // Función para añadir mensajes de feedback
  const addFeedbackMessage = (
    message: Omit<FeedbackMessage, 'id' | 'timestamp'>
  ) => {
    setFeedbackMessages(prev => [
      {
        ...message,
        id: Math.random().toString(36).substring(2, 11),
        timestamp: new Date()
      },
      ...prev
    ]); // Añadimos el nuevo mensaje al principio del array
  };

  // Función para resetear el estado
  const resetState = () => {
    setFormData({
      voice: voice || 'Bella',
      amount: debtamount?.toString() || '0',
      currency: currency || 'EUR',
      maxDiscount: '15',
      debtDescription: origin || 'Credit card debt',
      trn: trn || '',
      name: customer || '',
      dateofbirth: dateOfBirth
        ? new Date(dateOfBirth).toISOString().split('T')[0]
        : ''
    });
    setFeedbackMessages([]);
    setIsNifValidated(false);
    setIsDobValidated(false);
    setRequestTransfer(false);
    setClientInfo({
      voice: voice,
      trn: trn || '',
      name: customer || '',
      dateofbirth: dateOfBirth || new Date(),
      debtamount: debtamount || 0,
      currency: currency || 'EUR',
      origin: origin || '',
      IBAN: IBAN || 'GR16 0110 1050 0025 4801 2345 678',
      maxDiscount: maxDiscount || 10
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLElement>) => {
    const target = e.target as HTMLInputElement | HTMLSelectElement;
    const { name, value } = target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    setClientInfo({
      voice: formData.voice,
      trn: formData.trn,
      name: formData.name,
      dateofbirth: formData.dateofbirth
        ? new Date(formData.dateofbirth)
        : new Date(),
      debtamount: Number(formData.amount),
      currency: formData.currency,
      origin: formData.debtDescription,
      IBAN: IBAN || 'GR16 0110 1050 0025 4801 2345 678',
      maxDiscount: Number(formData.maxDiscount)
    });

    setIsNifValidated(false);
    setIsDobValidated(false);

    setFeedbackMessages(prev => [
      {
        id: Math.random().toString(36).substring(2, 11),
        timestamp: new Date(),
        type: 'validation',
        message: 'Customer information updated',
        details: `Name: ${formData.name} .... TRN: ${formData.trn} .... DOB: ${formData.dateofbirth} .... Debt: ${formData.amount} ${currency}`,
        success: true
      },
      ...prev
    ]);
  };

  const handleCloseModal = () => {
    resetState();
    onHide();
  };

  const handleTransfer = async () => {
    try {
      // Resetear todos los estados
      resetState();

      // Añadir mensaje de feedback sobre la transferencia
      const newFeedbackMessage = {
        id: Math.random().toString(36).substring(2, 11),
        timestamp: new Date(),
        type: 'close',
        message: 'Llamada finalizada',
        details: `TRN: ${formData.trn} - Nombre: ${formData.name}`,
        success: true
      } as FeedbackMessage;

      setFeedbackMessages(messages => [newFeedbackMessage, ...messages]);
    } catch (error) {
      console.error('Error al transferir la llamada:', error);
      onError?.(error as Error);
    }
  };

  const renderTransferAlert = () => {
    if (!requestTransfer) return null;

    return (
      <div className="d-flex align-items-center fs-9 pt-2">
        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
        <div className="flex-grow-1">Request to Transfer Call</div>
        <Button variant="warning" size="sm" onClick={handleTransfer}>
          Transfer Call
        </Button>
      </div>
    );
  };

  const renderFeedbackPanel = () => {
    return (
      <>
        <h5 className="pb-0 pt-4">{`${agentName}'s feedback`}</h5>
        <p className="fs-9 lh-sm">
          The panel below displays requests for information or tool usage made
          during the conversation.
        </p>
        <div
          className="p-3 pt-0 border rounded scrollbar bg-body-emphasis shadow-sm"
          style={{
            height: '180px',
            overflowY: 'auto',
            scrollBehavior: 'smooth'
          }}
        >
          {renderTransferAlert()}

          {/* Mensajes de feedback de las tools */}
          <div className="mt-3">
            {feedbackMessages.map(msg => (
              <div
                key={msg.id}
                className={`alert ${
                  msg.success ? 'bg-body' : 'alert-warning'
                } mb-2 py-2 px-3`}
                role="alert"
              >
                <div className="d-flex flex-column">
                  <small className="text-muted fs-10">
                    {msg.timestamp.toLocaleTimeString()}
                  </small>
                  <div className="fs-9 text-primary lh-1">{msg.message}</div>
                  {msg.details && (
                    <small className="mt-1 text-muted fs-10 lh-1">
                      {msg.details}
                    </small>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </>
    );
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="xl"
      animation={true}
      contentClassName="border-0"
    >
      <Modal.Header closeButton>
        <Modal.Title className="text-body-emphasis">
          {`${title} - ${agentName}`}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-0 pe-3 bg-body">
        <Row className="g-0">
          <h3 className="pb-0 pt-4 px-6">Test scenario</h3>
          <p className="fs-8 lh-sm px-6 mb-0">
            This test scenario demonstrates the interaction between the agent,
            the customer, and the system. The agent starts the call without any
            prior information, which is provided incrementally as needed during
            the interaction only if Tax Reference Number (TRN) and date of birth
            are veified first. This approach ensures that the agent does not
            have access to confidential information unless all security
            protocols have been successfully completed beforehand.
          </p>
          {/* Columna izquierda: Info inicial y solicitudes del agente durante la conversación */}
          <Col md={4} className="p-4 px-6 pe-0">
            <Form onSubmit={handleSubmit} className="w-100">
              <div className="d-flex flex-column gap-3">
                {/* Agent Configuration Section */}
                <div>
                  <h5 className="pb-0 pt-2">Agent Configuration</h5>
                  <p className="fs-9 lh-sm mb-2">
                    Configure the agent's voice and personality.
                  </p>
                  <Form.Floating>
                    <Form.Select
                      className="shadow-sm"
                      name="voice"
                      value={formData.voice}
                      onChange={handleInputChange}
                    >
                      <option value="V5hwa1mE7Lanym4smxt3">Ioanna</option>
                      <option value="aTP4J5SJLQl74WTSRXKW">Eleni</option>
                      <option value="Jv2zcgjn9Qu0uNMKJjb1">Madlen</option>
                    </Form.Select>
                    <label>Voice</label>
                  </Form.Floating>
                </div>

                {/* Context Configuration Section */}
                <div>
                  <h5 className="pb-0 pt-2">Call Context Configuration</h5>
                  <p className="fs-9 lh-sm mb-2">
                    Configure the debt context and negotiation parameters.
                  </p>
                  <div className="d-flex flex-column gap-2">
                    <div className="d-flex gap-2">
                      <Form.Floating className="flex-grow-1">
                        <Form.Control
                          className="shadow-sm"
                          type="number"
                          name="amount"
                          value={formData.amount}
                          onChange={handleInputChange}
                          placeholder="Amount"
                          min="0"
                          step="0.01"
                        />
                        <label>Amount</label>
                      </Form.Floating>

                      <Form.Floating style={{ minWidth: '100px' }}>
                        <Form.Select
                          className="shadow-sm"
                          name="currency"
                          value={formData.currency}
                          onChange={handleInputChange}
                        >
                          <option value="Euros">Euros</option>
                        </Form.Select>
                        <label>Currency</label>
                      </Form.Floating>
                    </div>

                    <Form.Floating>
                      <Form.Control
                        className="shadow-sm"
                        type="number"
                        name="maxDiscount"
                        value={formData.maxDiscount}
                        onChange={handleInputChange}
                        placeholder="Maximum discount"
                        min="0"
                        max="100"
                      />
                      <label>Discount on Card Payment (%)</label>
                    </Form.Floating>

                    <Form.Floating>
                      <Form.Control
                        className="shadow-sm"
                        as="textarea"
                        name="debtDescription"
                        value={formData.debtDescription}
                        onChange={handleInputChange}
                        placeholder="Debt description"
                        style={{ height: '70px' }}
                      />
                      <label>Debt description</label>
                    </Form.Floating>
                  </div>
                </div>

                {/* Data to Verify Section */}
                <div>
                  <h5 className="pb-0 pt-2">Customer data</h5>
                  <p className="fs-9 lh-sm mb-2">
                    The NIF and date of birth are not known to the agent. She
                    will only collect the information provided by the customer
                    for validation to the system.
                  </p>
                  <div className="d-flex flex-column gap-2">
                    <Form.Floating>
                      <Form.Control
                        className="shadow-sm"
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Full name"
                      />
                      <label>Full name</label>
                    </Form.Floating>

                    <div className="d-flex gap-2">
                      <Form.Floating className="flex-grow-1">
                        <Form.Control
                          className="shadow-sm"
                          type="text"
                          name="trn"
                          value={formData.trn}
                          onChange={handleInputChange}
                          placeholder="TRN"
                          isValid={isNifValidated}
                        />
                        <label>NIF</label>
                      </Form.Floating>

                      <Form.Floating className="flex-grow-1">
                        <Form.Control
                          className="shadow-sm"
                          type="date"
                          name="dateofbirth"
                          value={formData.dateofbirth}
                          onChange={handleInputChange}
                          placeholder="Birth date"
                          isValid={isDobValidated}
                        />
                        <label>Birth date</label>
                      </Form.Floating>
                    </div>
                  </div>
                </div>

                {/* Update Button */}
                <div className="d-flex justify-content-end">
                  <Button
                    type="submit"
                    variant="primary"
                    className="w-100 bg-primary"
                  >
                    Update
                  </Button>
                </div>
              </div>
            </Form>
          </Col>

          {/* Columna centro: Imagen */}
          <Col md={2} className="d-flex p-4 pe-0 pb-0 align-items-end">
            {agentImage && (
              <div className="text-center mb-2">
                <img
                  src={agentImage}
                  alt={agentName}
                  className="img-fluid rounded"
                  style={{
                    width: '100%',
                    minWidth: '163px',
                    maxWidth: '163px',
                    height: 'auto'
                  }}
                />
              </div>
            )}
          </Col>

          {/* Columna derecha: Datos dinámicos, widget de Conversación e información dinámica */}
          <Col md={6} className="p-4">
            <h5 className="pb-0 pt-2">Call with {agentName}</h5>
            <p className="fs-9 lh-sm mb-1">
              Press the button below to start a conversation with the agent.
            </p>
            <HVADynamicConversationWidget
              agentId={agentCode}
              agentName={agentName}
              onError={onError}
              onFeedback={addFeedbackMessage}
              firstMessage={firstMessage}
              prompt={prompt}
              voice={voice}
              language={language}
            />
            {renderFeedbackPanel()}
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="primary" onClick={handleCloseModal}>
          Close and reset
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default HVADynamicConversationModalContent;
